import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get } from "../api";
import { formatDay } from "../format";
import { Time } from "../Time";
import type { SourceList, SourceMessage, SourceThread } from "../api";

/**
 * The conversations a report was written from, in full.
 *
 * This is the most hostile input in the product: every character below was
 * written by somebody outside Arrowhead and collected by a poller. It is
 * safe here for one reason only — it reaches the DOM exclusively as text
 * nodes, which JSX escapes. **Nothing in this file may use
 * `dangerouslySetInnerHTML`**, and nothing may turn an ingested string into
 * an `href`, a `src` or a style value. `make web-check` greps for the first
 * of those, because it is the one that would look harmless in review.
 *
 * Bodies are deliberately untruncated. The agent reads a shortened copy to
 * keep its context affordable; a human checking whether a finding is real
 * should see what was actually said.
 */

/** Everything the section needs to show itself, and everything an evidence
    link needs to open a thread. One controller so both stay in step. */
export interface SourcesController {
  expanded: boolean;
  toggle: () => void;
  threads: SourceThread[] | null;
  loading: boolean;
  error: string | null;
  /** Thread ids we actually hold, once loaded. An evidence ref outside this
      set was invented by the agent. `null` until the fetch lands, when the
      honest answer is "not known yet" rather than "missing". */
  known: Set<string> | null;
  /** Open the section, the thread, and scroll to it — what an evidence link
      does. Safe to call before anything has loaded. */
  reveal: (threadId: string) => void;
  open: Set<string>;
  toggleThread: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  query: string;
  setQuery: (q: string) => void;
  onlyRead: boolean;
  setOnlyRead: (v: boolean) => void;
}

/**
 * Lazily fetches the source material and holds the section's state.
 *
 * Lazy on purpose: `/sources` re-reads every Slack message and email in the
 * report's window, which is by far the most expensive thing this page can
 * ask the server for, and most visits never open the section. Opening it —
 * or following an evidence link into it — is what triggers the fetch.
 */
export function useSources(customer: string, reportId: string): SourcesController {
  const [expanded, setExpanded] = useState(false);
  const [threads, setThreads] = useState<SourceThread[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [onlyRead, setOnlyRead] = useState(false);
  // A thread to scroll to as soon as it exists in the DOM, which is not
  // this tick: it may still be behind a fetch that has not landed.
  const [pending, setPending] = useState<string | null>(null);

  // The fetch's own bookkeeping, in refs rather than state: a guard that
  // lived in the dependency array of the effect that sets it cancelled the
  // request it had just started, and the section hung on "Reading the
  // window…" forever. Refs do not re-run anything.
  const held = useRef<SourceThread[] | null>(null);
  const inflight = useRef(false);
  // Which report a response belongs to. A slow window that lands after the
  // day picker moved on must not be shown against the report now on screen.
  const epoch = useRef(0);

  // Switching runs throws all of it away. Keeping the open set across a
  // change would reopen threads by id in a report that never had them.
  useEffect(() => {
    epoch.current += 1;
    held.current = null;
    inflight.current = false;
    setExpanded(false);
    setThreads(null);
    setLoading(false);
    setError(null);
    setOpen(new Set());
    setQuery("");
    setOnlyRead(false);
    setPending(null);
  }, [customer, reportId]);

  /**
   * Fetch the window, once.
   *
   * Called by whatever asks to see the material — the disclosure button or
   * an evidence link — rather than by an effect watching `expanded`.
   * Fetching here is an action, not a synchronization: the effect version
   * had to depend on the state it was itself setting, which is what made it
   * cancel its own request. Idempotent, so both callers may call it freely.
   */
  const load = useCallback(() => {
    if (held.current || inflight.current) return;
    inflight.current = true;
    const mine = epoch.current;
    setLoading(true);
    setError(null);
    void get<SourceList>(`/api/customers/${customer}/reports/${reportId}/sources`)
      .then((d) => {
        if (mine !== epoch.current) return;
        held.current = d.threads;
        setThreads(d.threads);
      })
      .catch((e) => {
        if (mine === epoch.current) setError((e as Error).message);
      })
      .finally(() => {
        if (mine !== epoch.current) return;
        // Cleared even on failure, so the next click retries rather than
        // finding a flag that says a request is still running.
        inflight.current = false;
        setLoading(false);
      });
  }, [customer, reportId]);

  // Scroll once the thread is actually on the page. Re-runs as the fetch
  // lands and as the thread opens, so it fires on whichever is last.
  useEffect(() => {
    if (!pending) return;
    const el = document.getElementById(`thread-${pending}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setPending(null);
  }, [pending, threads, open]);

  const reveal = useCallback(
    (threadId: string) => {
      setExpanded(true);
      load();
      setOpen((prev) => new Set(prev).add(threadId));
      // A filter that hides the thread we were just asked to show would make
      // the link look broken, so following one clears it.
      setQuery("");
      setOnlyRead(false);
      setPending(threadId);
    },
    [load],
  );

  // A link somebody pasted: /c/muthoot/report?r=<id>#thread-<uuid>.
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current) return;
    const hash = window.location.hash;
    if (!hash.startsWith("#thread-")) return;
    linked.current = true;
    reveal(hash.slice("#thread-".length));
  }, [reveal]);

  const known = useMemo(
    () => (threads ? new Set(threads.map((t) => t.id)) : null),
    [threads],
  );

  return {
    expanded,
    toggle: () => {
      if (!expanded) load();
      setExpanded((v) => !v);
    },
    threads,
    loading,
    error,
    known,
    reveal,
    open,
    toggleThread: (id) =>
      setOpen((prev) => {
        const next = new Set(prev);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    expandAll: () => setOpen(new Set((threads ?? []).map((t) => t.id))),
    collapseAll: () => setOpen(new Set()),
    query,
    setQuery,
    onlyRead,
    setOnlyRead,
  };
}

/** Searchable text per thread, built once rather than per keystroke: an
    appendix can carry a megabyte of message bodies. */
function haystack(t: SourceThread): string {
  return [
    t.title,
    t.participants.join(" "),
    ...t.messages.map((m) => `${m.author} ${m.subject ?? ""} ${m.body}`),
  ]
    .join("\n")
    .toLowerCase();
}

export default function Sources({ s }: { s: SourcesController }) {
  const indexed = useMemo(
    () => (s.threads ?? []).map((t) => ({ t, hay: haystack(t) })),
    [s.threads],
  );

  const needle = s.query.trim().toLowerCase();
  const shown = indexed.filter(
    ({ t, hay }) => (!needle || hay.includes(needle)) && (!s.onlyRead || t.read_by_agent),
  );

  const messages = (s.threads ?? []).reduce((n, t) => n + t.messages.length, 0);
  const read = (s.threads ?? []).filter((t) => t.read_by_agent).length;

  return (
    <section className="sources">
      <h2>
        <button className="disclose" onClick={s.toggle} aria-expanded={s.expanded}>
          <span className="caret">{s.expanded ? "▾" : "▸"}</span> Source material
        </button>
      </h2>

      {!s.expanded && (
        <p className="dim">
          Every conversation this report was written from, verbatim. Loaded on
          demand — it re-reads the whole window.
        </p>
      )}

      {s.expanded && s.error && <div className="error">{s.error}</div>}
      {s.expanded && s.loading && <div className="empty">Reading the window…</div>}

      {s.expanded && s.threads && (
        <>
          <p className="dim">
            {s.threads.length} conversations, {messages} messages. The agent opened{" "}
            {read} of them; the rest were listed in its survey and skipped.
          </p>

          <div className="controls">
            <input
              type="search"
              value={s.query}
              onChange={(e) => s.setQuery(e.target.value)}
              placeholder="Filter by subject, channel or person…"
              aria-label="Filter conversations"
            />
            <label className="check">
              <input
                type="checkbox"
                checked={s.onlyRead}
                onChange={(e) => s.setOnlyRead(e.target.checked)}
              />
              Only threads the agent read
            </label>
            <button type="button" onClick={s.expandAll}>
              Expand all
            </button>
            <button type="button" onClick={s.collapseAll}>
              Collapse all
            </button>
          </div>

          {shown.length === 0 ? (
            <p className="dim">No conversation matches that filter.</p>
          ) : (
            shown.map(({ t }) => (
              <Thread
                key={t.id}
                thread={t}
                open={s.open.has(t.id)}
                onToggle={() => s.toggleThread(t.id)}
              />
            ))
          )}
        </>
      )}
    </section>
  );
}

/** A day, or a span of them. Both ends are instants, so both go through the
    reader's timezone rather than being sliced out of the ISO string. */
function span(t: SourceThread): string {
  const from = formatDay(t.first_at);
  const to = formatDay(t.last_at);
  return from === to ? from : `${from} – ${to}`;
}

function Thread({
  thread: t,
  open,
  onToggle,
}: {
  thread: SourceThread;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <article className="thread" id={`thread-${t.id}`}>
      <button className="tsummary" onClick={onToggle} aria-expanded={open}>
        <span className="caret">{open ? "▾" : "▸"}</span>
        <span className={`pill src-${t.source}`}>{t.source === "slack" ? "Slack" : "Email"}</span>
        <span className={`pill ${t.read_by_agent ? "read" : "skipped"}`}>
          {t.read_by_agent ? "read" : "not read"}
        </span>
        <span className="ttitle">{t.title}</span>
        <span className="tmeta">
          {t.messages.length} msg · {span(t)}
        </span>
      </button>

      {/* Collapsed threads render nothing at all: an appendix can hold
          thousands of messages, and the filter searches the data rather
          than the DOM, so there is nothing to keep mounted for. */}
      {open && (
        <>
          <div className="tpeople">{t.participants.join(", ")}</div>
          {t.messages.map((m, i) => (
            <Message key={i} message={m} threadTitle={t.title} />
          ))}
        </>
      )}
    </article>
  );
}

function Message({ message: m, threadTitle }: { message: SourceMessage; threadTitle: string }) {
  return (
    <div className="msg">
      <div className="mhead">
        <span className="who">{m.author}</span>
        <span className="when">
          <Time value={m.at} />
        </span>
      </div>
      {/* Only when it differs from the thread's, or every email in a chain
          repeats its own title. */}
      {m.subject && m.subject !== threadTitle && <div className="msub">{m.subject}</div>}
      <div className="body">{m.body}</div>
    </div>
  );
}
